import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSending, setForgotSending] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    
    const { login, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Redirect if already logged in
    if (!authLoading && user) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) return;
        setForgotSending(true);
        try {
            await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail.trim() });
            setForgotSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible d'envoyer l'email");
        } finally {
            setForgotSending(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
                style={{
                    backgroundImage: 'url(https://images.pexels.com/photos/33049092/pexels-photo-33049092.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
                }}
            >
                <div className="absolute inset-0 bg-[#1B365D]/40"></div>
                <div className="relative z-10 flex flex-col justify-end p-12 text-white">
                    <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Bienvenue sur Luxy Move
                    </h2>
                    <p className="text-lg text-white/80">
                        Déménager en toute confiance
                    </p>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F3F4F6]">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_7977eb18-efae-4087-a922-da3158ec99c5/artifacts/5np07nb3_1763931680316.jpg" 
                            alt="Luxy Move" 
                            className="h-24 w-auto rounded-xl shadow-lg"
                        />
                    </div>

                    <Card className="border-0 shadow-xl">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle 
                                className="text-2xl text-center text-[#1B365D]"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                Connexion
                            </CardTitle>
                            <CardDescription className="text-center text-gray-500">
                                Entrez vos identifiants pour accéder à votre espace
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div 
                                        className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm"
                                        data-testid="login-error"
                                    >
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-gray-700">
                                        Email
                                    </Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="votre@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="pl-10 h-12 border-gray-300 focus:border-[#1B365D] focus:ring-[#1B365D]"
                                            data-testid="email-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-gray-700">
                                        Mot de passe
                                    </Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="pl-10 pr-10 h-12 border-gray-300 focus:border-[#1B365D] focus:ring-[#1B365D]"
                                            data-testid="password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            data-testid="toggle-password"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5" />
                                            ) : (
                                                <Eye className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-12 bg-[#1B365D] hover:bg-[#2A4A7A] text-white font-medium transition-all duration-200 active:scale-[0.98]"
                                    data-testid="login-submit"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Connexion...
                                        </span>
                                    ) : (
                                        'Se connecter'
                                    )}
                                </Button>

                                <div className="text-center pt-1">
                                    <button
                                        type="button"
                                        onClick={() => { setForgotEmail(email); setForgotSent(false); setForgotOpen(true); }}
                                        className="text-sm text-[#1B365D] hover:text-[#2A4A7A] hover:underline transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        Mot de passe oublié ?
                                    </button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    <p className="mt-6 text-center text-sm text-gray-500">
                        © 2024 Luxy Move. Tous droits réservés.
                    </p>
                </div>
            </div>

            {/* Forgot password modal */}
            <Dialog open={forgotOpen} onOpenChange={(v) => { if (!forgotSending) setForgotOpen(v); }}>
                <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="forgot-password-modal">
                    <DialogTitle className="sr-only">Mot de passe oublié</DialogTitle>
                    {!forgotSent ? (
                        <form onSubmit={handleForgotSubmit} className="p-6 space-y-5">
                            <div className="flex items-center gap-2">
                                <Lock className="w-5 h-5 text-[#1B365D]" />
                                <h2 className="text-lg font-bold text-[#1B365D]">Mot de passe oublié</h2>
                            </div>
                            <p className="text-sm text-gray-500">Saisissez l'adresse email associée à votre compte. Nous vous enverrons un lien pour réinitialiser votre mot de passe à votre adresse de récupération.</p>
                            <div className="space-y-2">
                                <Label className="text-sm text-gray-700">Email du compte</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        type="email"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="votre@email.com"
                                        required
                                        className="pl-9"
                                        data-testid="forgot-email-input"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} disabled={forgotSending}>
                                    Annuler
                                </Button>
                                <Button type="submit"
                                    className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                                    disabled={forgotSending || !forgotEmail.trim()}
                                    data-testid="forgot-submit">
                                    {forgotSending ? 'Envoi...' : 'Envoyer le lien'}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <div className="p-8 text-center space-y-4" data-testid="forgot-success">
                            <div className="text-5xl">✉️</div>
                            <h2 className="text-lg font-bold text-[#1B365D]">Email envoyé</h2>
                            <p className="text-sm text-gray-500">Si cette adresse correspond à un compte, un email contenant le lien de réinitialisation a été envoyé. Vérifiez votre boîte de réception (et vos spams).</p>
                            <Button className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white w-full" onClick={() => setForgotOpen(false)}>
                                Fermer
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
